import React, {useCallback, useMemo, useState} from "react";
import {declineOrderOptions, declineOrderOptionsToText} from "./consts.js";
import {FormModal} from "../../components/index.jsx";
import {Images} from "../../images/Images.jsx";
import {ConfigProvider, Checkbox} from "antd";
import "./decline-order-modal.css";
import {editOrderNotificationsOption, editOrderNotificationsReceivers} from "../../orders/orderModals/consts.jsx";

const {noteImage} = Images;

const defaultWhoToSendUpdateEmailOptions = [
    editOrderNotificationsReceivers.provider,
    editOrderNotificationsReceivers.company
];

export const DeclineOrderModal = ({
    visible,
    onClose,
    onSave,
    onContactClick,
    declineReasons = null,
    header = "יש להוסיף עבור הלקוח את סיבת הסירוב להזמנה",
    subTitle = null,
    showWhoToNotify = false,
    defaultWhoToSendUpdateEmail = defaultWhoToSendUpdateEmailOptions,
    declineReasonLabel = "בחרו את סיבת הסירוב שתשלח ללקוח",
    declineCommentLabel = "הסבר נוסף לסיבת הסירוב"
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [whoToSendUpdateEmail, setWhoToSendUpdateEmail] = useState(
        defaultWhoToSendUpdateEmail ?? defaultWhoToSendUpdateEmailOptions
    );

    const onFinish = useCallback(
        async declineDetails => {
            setIsLoading(true);
            const detailsToSave = {
                ...declineDetails,
                ...(showWhoToNotify ? {whoToSendUpdateEmail} : {})
            };
            await onSave(detailsToSave);
            setIsLoading(false);
        },
        [onSave, showWhoToNotify, whoToSendUpdateEmail]
    );

    const declineOrderReasons = useMemo(() => {
        if (declineReasons) {
            return Object.entries(declineReasons).map(([key, value]) => ({name: key, label: value}));
        }
        return Object.entries(declineOrderOptionsToText).map(([key, value]) => ({name: key, label: value}));
    }, [declineReasons]);

    const notificationsOptions = useMemo(
        () => editOrderNotificationsOption.filter(o => defaultWhoToSendUpdateEmail.includes(o.value)),
        [defaultWhoToSendUpdateEmail]
    );

    const onWhoToSendUpdateEmailChange = useCallback(
        values => {
            const stringValues = Array.isArray(values) ? values : [];
            setWhoToSendUpdateEmail(stringValues);
        },
        [setWhoToSendUpdateEmail]
    );

    return (
        <div>
            <ConfigProvider direction="rtl">
                <FormModal
                    className="decline-order-modal-container"
                    image={noteImage}
                    header={header}
                    subTitle={
                        <>
                            {subTitle ? <div style={{textAlign: "center"}}>{subTitle}</div> : null}
                            {onContactClick ? (
                                <a className="decline-order-modal-container-help-link" onClick={onContactClick}>
                                    לבירור מול מנהלת הלקוח לחצו כאן
                                </a>
                            ) : null}
                        </>
                    }
                    visible={visible}
                    onClose={() => {
                        setWhoToSendUpdateEmail(defaultWhoToSendUpdateEmail ?? defaultWhoToSendUpdateEmailOptions);
                        onClose();
                    }}
                    onSave={onFinish}
                    isConfirmButtonLoading={isLoading}
                    fields={[
                        {
                            name: "declineReason",
                            label: declineReasonLabel,
                            type: "select",
                            options: declineOrderReasons,
                            placeholder: "בחרו את אחת האפשרויות"
                        },
                        {
                            name: "declineComment",
                            className: "declineComment",
                            ...(declineCommentLabel ? {label: declineCommentLabel} : {}),
                            type: "textarea",
                            rows: 5,
                            placeholder: "ניתן להוסיף כאן",
                            optional: true
                        },
                        ...(showWhoToNotify
                            ? [
                                  {
                                      name: "whoToSendUpdateEmail",
                                      label: "למי לשלוח עדכון?",
                                      type: "custom",
                                      customComponent: (
                                          <div className="decline-order-modal-who-to-notify-container">
                                              <Checkbox.Group
                                                  onChange={onWhoToSendUpdateEmailChange}
                                                  options={notificationsOptions}
                                                  value={whoToSendUpdateEmail}
                                                  defaultValue={defaultWhoToSendUpdateEmail}
                                                  className="decline-order-modal-who-to-notify-checkbox-group"
                                              />
                                          </div>
                                      )
                                  }
                              ]
                            : [])
                    ]}
                />
            </ConfigProvider>
        </div>
    );
};
